// VisitReport wizard engine — the state machine + submit flow hooks. Extracted
// from VisitReportWizard.tsx (size budget). Pure logic, no JSX. Behaviour
// unchanged; see VisitReportWizard.tsx for the orchestrator that consumes this.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MobilityStatus, VisitType } from "@hcm/core";
import { ApiError } from "../lib/api.js";
import {
  type FormState, type Action, INIT, set,
} from "../lib/visit-report-types.js";
import { buildPayload } from "../lib/visit-report-payload.js";
import {
  submitVisitReport, extractSectionPayload, patchSrVisitType,
  type SectionName, type Fetcher,
} from "../api/visit-report-api.js";
import {
  visibleSteps, visibleSectionSteps,
  type ConditionalContext, type WizardStep, type SectionStep,
} from "../lib/visit-report-conditional-rules.js";
import {
  parseServerError, routeFieldErrorsToSections,
} from "../lib/visit-report-error-mapping.js";
import {
  FALLBACK_FIRST_STEP, VISIT_TYPE_STEP_ID, isSectionStep,
  type ServerErrorsBySection,
} from "./visit-report-wizard-helpers.js";

function appendUnique<T>(set: ReadonlySet<T>, value: T): ReadonlySet<T> {
  if (set.has(value)) return set;
  const next = new Set(set);
  next.add(value);
  return next;
}

function extractApiBodyMessage(body: unknown, fallback: string): string {
  if (typeof body === "string") return body;
  if (body && typeof body === "object") {
    const obj = body as { error?: unknown; errors?: unknown };
    if (typeof obj.error === "string") return obj.error;
    if (Array.isArray(obj.errors)) {
      return obj.errors
        .map((e: unknown) => (e as { message?: string }).message ?? JSON.stringify(e))
        .join("; ");
    }
    return JSON.stringify(body);
  }
  return fallback;
}

function formatServerError(err: unknown): string {
  if (err instanceof ApiError) return extractApiBodyMessage(err.body, err.message);
  return err instanceof Error ? err.message : "Submission failed.";
}

export interface ActiveMedsResult {
  hasActiveMeds: boolean;
  failed: boolean;
  loading: boolean;
}

export function useActiveMedsCount(patientId: string, fetcher: Fetcher): ActiveMedsResult {
  const [state, setState] = useState<ActiveMedsResult>({ hasActiveMeds: false, failed: false, loading: true });
  useEffect(() => {
    let cancelled = false;
    fetcher<{ data: unknown[]; total: number }>(`/patients/${patientId}/medications?status=active`)
      .then((res) => { if (!cancelled) setState({ hasActiveMeds: (res.total ?? 0) > 0, failed: false, loading: false }); })
      .catch(() => { if (!cancelled) setState({ hasActiveMeds: false, failed: true, loading: false }); });
    return () => { cancelled = true; };
  }, [patientId, fetcher]);
  return state;
}

interface FrozenSteps {
  steps: readonly WizardStep[];
  currentStep: WizardStep;
  setCurrentStep: (s: WizardStep) => void;
  visitType: VisitType | null;
  setVisitType: (v: VisitType) => void;
  overrideShowMobility: boolean;
  setOverrideShowMobility: (v: boolean) => void;
  recompute: () => void;
  contextRef: React.MutableRefObject<ConditionalContext | null>;
}

// D6 freeze — captures baselineMobility + initialVisitType at mount in
// state. Two legitimate mutation paths: (1) setVisitType after step-0
// PATCH; (2) setOverrideShowMobility when the carer toggles the
// mobility override checkbox. Both call recompute() to bump the
// memo so visibleSteps re-evaluates with the new ConditionalContext.
function useFrozenSteps(
  baselineMobility: MobilityStatus | null | undefined,
  initialVisitType: VisitType | null | undefined,
  meds: ActiveMedsResult,
): FrozenSteps {
  const [frozenBaselineMobility] = useState<MobilityStatus | null | undefined>(baselineMobility);
  const [visitType, setVisitType] = useState<VisitType | null>(initialVisitType ?? null);
  const [overrideShowMobility, setOverrideShowMobility] = useState(false);
  const [recomputeVersion, setRecomputeVersion] = useState(0);
  const contextRef = useRef<ConditionalContext | null>(null);

  const steps = useMemo<readonly WizardStep[]>(() => {
    const ctx: ConditionalContext = {
      baselineMobility: frozenBaselineMobility,
      visitType,
      hasActiveMeds: meds.hasActiveMeds,
      medsQueryFailed: meds.failed,
      overrideShowMobility,
    };
    contextRef.current = ctx;
    return visibleSteps(ctx);
  }, [frozenBaselineMobility, visitType, meds.hasActiveMeds, meds.failed, overrideShowMobility, recomputeVersion]);

  const [currentStep, setCurrentStep] = useState<WizardStep>(steps[0] ?? FALLBACK_FIRST_STEP);

  useEffect(() => {
    if (!steps.includes(currentStep)) {
      setCurrentStep(steps[0] ?? FALLBACK_FIRST_STEP);
    }
  }, [steps, currentStep]);

  return {
    steps, currentStep, setCurrentStep, visitType, setVisitType,
    overrideShowMobility, setOverrideShowMobility,
    recompute: () => setRecomputeVersion((v) => v + 1),
    contextRef,
  };
}

export interface WizardEngine {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  dispatch: (action: Action) => void;
  currentStep: WizardStep;
  steps: readonly WizardStep[];
  completedSteps: ReadonlySet<string>;
  serverError: string | null;
  isSaving: boolean;
  visitType: VisitType | null;
  overrideShowMobility: boolean;
  setOverrideShowMobility: (v: boolean) => void;
  contextRef: React.MutableRefObject<ConditionalContext | null>;
  /** Current section's payload subset, computed from latest form state. */
  currentSectionPayload: () => Record<string, unknown>;
  markComplete: (id: WizardStep) => void;
  setServerError: (msg: string | null) => void;
  handleStepNav: (target: WizardStep) => void;
  handleSubmit: () => Promise<void>;
  handleVisitTypePicked: (visitType: VisitType) => Promise<void>;
  /** Q9 S6 — section → API field name → error messages (set on submit-422). */
  serverFieldErrors: ServerErrorsBySection;
  setCurrentStep: (s: WizardStep) => void;
  /** Q9 S7 — pending focus target {section, fieldName}; cleared on next dispatch. */
  pendingFocus: { section: SectionStep; fieldName: string } | null;
  requestFocus: (section: SectionStep, fieldName: string) => void;
}

interface SubmitErrorRouting {
  firstSection: SectionStep | null;
  firstFieldName: string | null;
}

interface UseValidationStateResult {
  serverFieldErrors: ServerErrorsBySection;
  recordSubmitErrors: (errors: ReadonlyMap<string, readonly string[]>) => SubmitErrorRouting;
  clearSectionErrors: (section: SectionStep) => void;
  clearAll: () => void;
}

// Q9 S6 / D8 8a — server field errors keyed by owning section. Routes a
// 422's flat field-error map into per-section maps via getFieldSection;
// returns the FIRST owning section + the first field name within it so
// the wizard can auto-navigate AND focus the offending input (S7 polish).
function useValidationState(): UseValidationStateResult {
  const [serverFieldErrors, setServer] = useState<ServerErrorsBySection>(new Map());
  const recordSubmitErrors = useCallback((errors: ReadonlyMap<string, readonly string[]>): SubmitErrorRouting => {
    const { bySection } = routeFieldErrorsToSections(errors);
    setServer(bySection);
    const firstSectionEntry = bySection.entries().next();
    if (firstSectionEntry.done) return { firstSection: null, firstFieldName: null };
    const [firstSection, fieldMap] = firstSectionEntry.value;
    const firstFieldEntry = fieldMap.keys().next();
    return {
      firstSection,
      firstFieldName: firstFieldEntry.done ? null : firstFieldEntry.value,
    };
  }, []);
  const clearSectionErrors = useCallback((section: SectionStep): void => {
    setServer((prev) => {
      if (!prev.get(section)?.size) return prev;
      const next = new Map(prev); next.delete(section); return next;
    });
  }, []);
  const clearAll = useCallback((): void => setServer(new Map()), []);
  return { serverFieldErrors, recordSubmitErrors, clearSectionErrors, clearAll };
}

interface SubmitArgs {
  srId: string; submissionChannel: "crm" | "mobile";
  contextRef: React.MutableRefObject<ConditionalContext | null>;
  baselineMobility: MobilityStatus | null | undefined;
  visitType: VisitType | null; meds: ActiveMedsResult; overrideShowMobility: boolean;
  fetcher: Fetcher;
}

async function performSubmit(
  args: SubmitArgs, currentStep: WizardStep,
  currentSectionPayload: () => Record<string, unknown>,
  dispatchSave: (s: SectionStep, p: Record<string, unknown>) => Promise<void>,
): Promise<void> {
  if (isSectionStep(currentStep)) {
    await dispatchSave(currentStep as SectionStep, currentSectionPayload());
  }
  const submitVisible = visibleSectionSteps(args.contextRef.current ?? {
    baselineMobility: args.baselineMobility, visitType: args.visitType,
    hasActiveMeds: args.meds.hasActiveMeds, medsQueryFailed: args.meds.failed,
    overrideShowMobility: args.overrideShowMobility,
  });
  await submitVisitReport(args.srId, {
    completed_at: new Date().toISOString(),
    submission_channel: args.submissionChannel,
    visible_steps: submitVisible,
  }, args.fetcher);
}

export function useVisitReportWizard(
  srId: string, submissionChannel: "crm" | "mobile",
  onSubmitted: () => void, initialFormState: FormState | undefined,
  baselineMobility: MobilityStatus | null | undefined,
  initialVisitType: VisitType | null | undefined,
  meds: ActiveMedsResult,
  dispatchSave: (section: SectionStep, payload: Record<string, unknown>) => Promise<void>,
  fetcher: Fetcher,
): WizardEngine {
  const [form, setForm] = useState<FormState>(initialFormState ?? INIT);
  const [completedSteps, setCompletedSteps] = useState<ReadonlySet<string>>(new Set());
  const [serverError, setServerError] = useState<string | null>(null);
  // isSaving is owned by useSubmitFlow now (S6); local placeholder removed.
  const validation = useValidationState();
  const [pendingFocus, setPendingFocus] = useState<{ section: SectionStep; fieldName: string } | null>(null);

  const stepEngine = useFrozenSteps(baselineMobility, initialVisitType, meds);
  const {
    steps, currentStep, setCurrentStep, visitType, setVisitType,
    overrideShowMobility, setOverrideShowMobility, recompute, contextRef,
  } = stepEngine;

  const formRef = useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);

  // Q9 S6 / D8 — "Persists until the field changes": clear server errors
  // for the section the carer is editing on the next dispatch, so the
  // red highlights disappear as soon as the carer types. Q9 S7: also
  // clear the pending focus once the carer engages with any field.
  const dispatch = useCallback((action: Action): void => {
    setForm((f) => set(f, action.field, action.value as never));
    if (isSectionStep(currentStep)) {
      validation.clearSectionErrors(currentStep as SectionStep);
    }
    setPendingFocus(null);
  }, [currentStep, validation]);

  const markComplete = useCallback((id: WizardStep): void => {
    setCompletedSteps((prev) => appendUnique(prev, id));
  }, []);

  const currentSectionPayload = useCallback((): Record<string, unknown> => {
    if (!isSectionStep(currentStep)) return {};
    return extractSectionPayload(buildPayload(srId, formRef.current), currentStep as SectionName);
  }, [currentStep, srId]);

  const handleStepNav = (target: WizardStep): void => {
    if (target === currentStep) return;
    if (isSectionStep(currentStep)) {
      markComplete(currentStep);
      void dispatchSave(currentStep as SectionStep, currentSectionPayload());
    }
    setCurrentStep(target);
    setPendingFocus(null);
  };

  const requestFocus = useCallback((section: SectionStep, fieldName: string): void => {
    setPendingFocus({ section, fieldName });
  }, []);

  const flow = useSubmitFlow({
    srId, submissionChannel, currentStep, currentSectionPayload, dispatchSave,
    contextRef, baselineMobility, visitType, meds, overrideShowMobility,
    setVisitType, setCurrentStep, markComplete, recompute, setServerError,
    onSubmitted, validation, requestFocus, fetcher,
  });

  return {
    form, setForm, dispatch, currentStep, steps, completedSteps,
    serverError, isSaving: flow.isSubmitting,
    visitType, overrideShowMobility, setOverrideShowMobility,
    contextRef, currentSectionPayload, markComplete, setServerError,
    handleStepNav, handleSubmit: flow.handleSubmit,
    handleVisitTypePicked: flow.handleVisitTypePicked,
    serverFieldErrors: validation.serverFieldErrors, setCurrentStep,
    pendingFocus, requestFocus,
  };
}

interface SubmitFlowArgs {
  srId: string; submissionChannel: "crm" | "mobile";
  currentStep: WizardStep;
  currentSectionPayload: () => Record<string, unknown>;
  dispatchSave: (s: SectionStep, p: Record<string, unknown>) => Promise<void>;
  contextRef: React.MutableRefObject<ConditionalContext | null>;
  baselineMobility: MobilityStatus | null | undefined;
  visitType: VisitType | null; meds: ActiveMedsResult; overrideShowMobility: boolean;
  setVisitType: (v: VisitType) => void;
  setCurrentStep: (s: WizardStep) => void;
  markComplete: (id: WizardStep) => void;
  recompute: () => void;
  setServerError: (msg: string | null) => void;
  onSubmitted: () => void;
  validation: UseValidationStateResult;
  /** Q9 S7 — request a post-422 focus + scroll to the first errored field. */
  requestFocus: (section: SectionStep, fieldName: string) => void;
  /** Carer-parity S3a — used by submit + visit-type PATCH. */
  fetcher: Fetcher;
}

function useSubmitFlow(args: SubmitFlowArgs): {
  isSubmitting: boolean;
  handleSubmit: () => Promise<void>;
  handleVisitTypePicked: (v: VisitType) => Promise<void>;
} {
  const [isSubmitting, setSubmitting] = useState(false);
  const onSubmitError = useCallback((err: unknown): void => {
    const parsed = parseServerError(err);
    if (parsed.fieldErrors.size === 0) {
      args.setServerError(parsed.fallback ?? formatServerError(err)); return;
    }
    const { firstSection, firstFieldName } = args.validation.recordSubmitErrors(parsed.fieldErrors);
    if (firstSection && firstSection !== args.currentStep) args.setCurrentStep(firstSection);
    if (firstSection && firstFieldName) args.requestFocus(firstSection, firstFieldName);
    args.setServerError(parsed.formErrors.length > 0 ? parsed.formErrors.join("; ") : null);
  }, [args]);

  const handleSubmit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await performSubmit(args, args.currentStep, args.currentSectionPayload, args.dispatchSave);
      args.validation.clearAll(); args.setServerError(null); args.onSubmitted();
    } catch (err) { onSubmitError(err); } finally { setSubmitting(false); }
  };

  const handleVisitTypePicked = async (picked: VisitType): Promise<void> => {
    setSubmitting(true);
    try {
      await patchSrVisitType(args.srId, picked, args.fetcher);
      args.setVisitType(picked); args.markComplete(VISIT_TYPE_STEP_ID); args.recompute();
      args.setServerError(null); args.setCurrentStep(FALLBACK_FIRST_STEP);
    } catch (err) {
      args.setServerError(formatServerError(err));
    } finally { setSubmitting(false); }
  };

  return { isSubmitting, handleSubmit, handleVisitTypePicked };
}
